import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { BANNER_API_BASE_URL, getApiHeaders } from '../config/api';

type Banner = {
  _id: string;
  imageUrl: string;
  navUrl: string;
  navType?: 'WEB' | 'SCREEN';
  priority: number;
  isActive: boolean;
  expiresAt?: string;
};

type Section = {
  _id: string;
  sectionNumber: number;
  maxDisplay: number;
  banners: Banner[];
};

const SectionDetails: React.FC = () => {
  const { screen, sectionId } = useParams<{ screen: string; sectionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState<Section | null>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('wallet_assets_user_id');
    
    if (!token) return null;
    if (!userId) return null;
    
    const baseHeaders = getApiHeaders();
    return {
      'x-api-key': baseHeaders['x-api-key'],
      'userid': userId,
      'accesstoken': token,
    };
  }, []);

  const fetchSectionDetails = useCallback(async () => {
    if (!screen || !sectionId) return;
    
    const headers = headerWithAuth();
    if (!headers) {
      toast.error('Authentication failed');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BANNER_API_BASE_URL}/banners/${screen}`, {
        method: 'GET',
        headers,
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to fetch banners');

      const screenData = json.data;
      const dataToSearch = Array.isArray(screenData) ? screenData.find((s: any) => s.screen === screen) : screenData;

      if (!dataToSearch) throw new Error('Screen not found');

      const foundSection = dataToSearch.sections.find((s: any) => s._id === sectionId);
      if (!foundSection) throw new Error('Section not found');

      setSection(foundSection);
    } catch (error) {
      toast.error((error as Error).message);
      navigate('/banners');
    } finally {
      setLoading(false);
    }
  }, [screen, sectionId, headerWithAuth, navigate]);

  useEffect(() => {
    fetchSectionDetails();
  }, [fetchSectionDetails]);

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white'></div>
      </div>
    );
  }

  if (!section) return null;

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900'>
      <Navbar />
      
      <div className='container mx-auto px-4 py-8'>
        {/* Header */}
        <div className='mb-8 flex items-center justify-between'>
          <div>
            <button
              onClick={() => navigate('/banners')}
              className='flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-4'
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
              </svg>
              Back to Banners
            </button>
            <h1 className='text-3xl font-bold text-white'>Section {section.sectionNumber}</h1>
            <p className='text-gray-400 mt-1'>
              Screen: {screen} &bull; Max Display: {section.maxDisplay}
            </p>
          </div>
        </div>

        {/* Banners Grid */}
        <div className='bg-white/5 border border-white/10 rounded-xl p-6'>
          <h2 className='text-xl font-semibold text-white mb-6'>Banners in Section</h2>
          
          {section.banners.length > 0 ? (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {section.banners.map((banner) => (
                <div 
                  key={banner._id} 
                  className='bg-white/5 border border-white/10 rounded-lg overflow-hidden hover:border-purple-500/50 transition-all group cursor-pointer'
                  onClick={() => navigate(`/banners/${screen}/section/${section._id}/banner/${banner._id}`)}
                >
                  <div className='relative aspect-video bg-gray-800'>
                    <img
                      src={banner.imageUrl}
                      alt='Banner'
                      className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300'
                    />
                    <div className='absolute top-2 left-2 px-2 py-1 rounded bg-black/60 text-white text-xs font-semibold'>
                      Priority {banner.priority}
                    </div>
                    {banner.expiresAt && (
                      <div className='absolute top-2 right-2 px-2 py-1 rounded bg-orange-500/80 text-white text-xs font-semibold'>
                        Expires {new Date(banner.expiresAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  <div className='p-4'>
                    <div className='flex items-start justify-between mb-2'>
                      <div className='flex-1 pr-2'>
                        <p className='text-sm text-gray-400 mb-1'>Navigation:</p>
                        <p className='text-white text-sm font-mono truncate'>{banner.navUrl}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        banner.navType === 'WEB' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'
                      }`}>
                        {banner.navType || 'WEB'}
                      </span>
                    </div>

                    <div className='flex items-center justify-between mt-3'>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        banner.isActive ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {banner.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-12 text-gray-500'>
              No banners in this section
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionDetails;
